import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { dictionariesService } from '@/services';

interface UseFinanceProps {
  type: 'wallets' | 'accounts' | 'currencies' | 'countries';
}

export const useFinance = ({ type }: UseFinanceProps) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [currencies, setCurrencies] = useState<any[]>([]);
  const dispatch = useDispatch();

  const fetchData = async () => {
    try {
      setLoading(true);
      let response;
      switch (type) {
        case 'wallets':
          response = await dictionariesService.getPaymentSystems();
          break;
        case 'accounts':
          response = await dictionariesService.getBanks();
          break;
        case 'currencies':
          response = await dictionariesService.getCurrencies();
          break;
        case 'countries':
          response = await dictionariesService.getCountries();
          break;
      }
      setData(response);
    } catch (err) {
      setError('Ошибка при загрузке данных');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await dictionariesService.getCurrencies();
      setCurrencies(response);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = () => {
    setFormData(type === 'wallets' ? { supports_payout_out: false } : {});
    setDialogOpen(true);
  };

  const handleEdit = (item: any) => {
    setFormData(item);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string | number) => {
    try {
      switch (type) {
        case 'wallets':
          await dictionariesService.deletePaymentSystem(id as string);
          break;
        case 'accounts':
          await dictionariesService.deleteBank(id as string);
          break;
        case 'currencies':
          await dictionariesService.deleteCurrency(id as string);
          break;
        case 'countries':
          await dictionariesService.deleteCountry(id as string);
          break;
      }
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const buildPaymentSystemPayload = (fd: Record<string, any>) => {
    const payload = { ...fd };
    delete payload.direction;
    return payload;
  };

  /** После создания ПС возвращает созданный объект для второго шага (настройки IN/OUT). */
  const handleSubmit = async (): Promise<{ createdPaymentSystem?: any } | void> => {
    try {
      if (formData.id) {
        switch (type) {
          case 'wallets':
            await dictionariesService.updatePaymentSystem(
              formData.id,
              buildPaymentSystemPayload(formData),
            );
            break;
          case 'accounts':
            await dictionariesService.updateBank(formData.id, formData);
            break;
          case 'currencies':
            await dictionariesService.updateCurrency(formData.id, formData);
            break;
          case 'countries':
            await dictionariesService.updateCountry(formData.id, formData);
            break;
        }
      } else {
        switch (type) {
          case 'wallets': {
            const created = await dictionariesService.createPaymentSystem(
              buildPaymentSystemPayload(formData),
            );
            setDialogOpen(false);
            await fetchData();
            if (created?.id) {
              return { createdPaymentSystem: created };
            }
            return;
          }
          case 'accounts':
            await dictionariesService.createBank(formData);
            break;
          case 'currencies':
            await dictionariesService.createCurrency(formData);
            break;
          case 'countries':
            await dictionariesService.createCountry(formData);
            break;
        }
      }
      setDialogOpen(false);
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    fetchData();
    fetchCurrencies();
  }, [type]);

  return {
    data,
    loading,
    error,
    dialogOpen,
    formData,
    currencies,
    setDialogOpen,
    handleCreate,
    handleEdit,
    handleDelete,
    handleSubmit,
    handleFormChange,
    refetch: fetchData,
  };
}; 